import { useEffect, useRef, useState } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

import { WS_BASE_URL } from '../api/config';
import { authService } from '../services/auth';
import type { ModelLabTrainingJob, PaginatedResponse } from '../types';

type TrainingJobsScope = number | 'all' | null | undefined;

interface UseModelLabTrainingJobsSocketOptions {
  modelAssetId: TrainingJobsScope;
  enabled: boolean;
  queryKey: QueryKey;
  pageSize?: number;
}

const emptyPage = (): PaginatedResponse<ModelLabTrainingJob> => ({
  count: 0,
  next: null,
  previous: null,
  results: [],
});

const sortJobs = (jobs: ModelLabTrainingJob[]) => (
  [...jobs].sort((a, b) => Date.parse(b.created_at || '') - Date.parse(a.created_at || ''))
);

const upsertJob = (
  current: PaginatedResponse<ModelLabTrainingJob> | undefined,
  job: ModelLabTrainingJob,
  pageSize: number,
): PaginatedResponse<ModelLabTrainingJob> => {
  const base = current || emptyPage();
  const index = base.results.findIndex((item) => item.id === job.id);
  const results = index >= 0
    ? base.results.map((item) => (item.id === job.id ? { ...item, ...job } : item))
    : [job, ...base.results];

  return {
    ...base,
    count: index >= 0 ? base.count : Math.max(base.count + 1, results.length),
    results: sortJobs(results).slice(0, pageSize),
  };
};

export const useModelLabTrainingJobsSocket = ({
  modelAssetId,
  enabled,
  queryKey,
  pageSize = 50,
}: UseModelLabTrainingJobsSocketOptions) => {
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const scope = modelAssetId ?? null;
    const shouldConnect = Boolean(enabled && scope);
    let closedByEffect = false;

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current != null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const closeSocket = () => {
      const socket = socketRef.current;
      socketRef.current = null;
      if (socket && socket.readyState !== WebSocket.CLOSED) {
        socket.close(1000, 'monitor closed');
      }
    };

    if (!shouldConnect) {
      setIsConnected(false);
      clearReconnectTimer();
      closeSocket();
      return () => undefined;
    }

    const connect = () => {
      const token = authService.getAccessToken();
      if (!token || !WS_BASE_URL) {
        setIsConnected(false);
        return;
      }

      closeSocket();
      const socket = new WebSocket(
        `${WS_BASE_URL}/ws/model-lab/training-jobs/${scope}/?token=${encodeURIComponent(token)}`,
      );
      socketRef.current = socket;

      socket.onopen = () => {
        setIsConnected(true);
        void queryClient.invalidateQueries({ queryKey });
      };

      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'training_jobs.snapshot') {
            queryClient.setQueryData<PaginatedResponse<ModelLabTrainingJob>>(queryKey, {
              count: Number(message.count || 0),
              next: message.next ?? null,
              previous: message.previous ?? null,
              results: Array.isArray(message.results) ? message.results : [],
            });
          } else if (message.type === 'training_job.updated' && message.job) {
            queryClient.setQueryData<PaginatedResponse<ModelLabTrainingJob>>(
              queryKey,
              (current) => upsertJob(current, message.job, pageSize),
            );
            queryClient.setQueryData<ModelLabTrainingJob>(
              ['model-lab-training-job', message.job.id],
              (current) => (current ? { ...current, ...message.job, logs: message.job.logs ?? current.logs } : message.job),
            );
          }
        } catch {
          // Ignore malformed socket payloads; the initial HTTP query remains the fallback.
        }
      };

      socket.onerror = () => {
        setIsConnected(false);
      };

      socket.onclose = () => {
        setIsConnected(false);
        if (!closedByEffect) {
          reconnectTimerRef.current = window.setTimeout(connect, 5000);
        }
      };
    };

    connect();

    return () => {
      closedByEffect = true;
      clearReconnectTimer();
      closeSocket();
      setIsConnected(false);
    };
  }, [enabled, modelAssetId, pageSize, queryClient, queryKey]);

  return { isConnected };
};

export default useModelLabTrainingJobsSocket;
